import { ModalAction } from '../ModalAction'
import { useCoachingString } from '../useCoachingString'
import { assertWriteIdentity, captureWriteIdentity } from '@/core/api/http'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useApp } from '@/core/state/appStore'
import { traegerbestaetigungForm } from '../../../templates'
import { initialFormValues, type FormValues } from '@/core/components/form/formModel'
import {
  FormRenderer,
  type FieldHints,
  type FieldHint,
  type DateFieldLimits,
} from '@/core/components/form/FormRenderer'
import { setAt, getAt } from '@/core/lib/path'
import { friendlyErrorMessage } from '@/core/lib/dbError'
import { gateAdvance } from '@/core/lib/advanceGate'
import { useAutosave } from '@/core/hooks/useAutosave'
import { tbManualFields, generateFilledTb, loadTbForm, loadTbErfassen, saveTbDraft, saveTbForm, checkTb, confirmTb, getTbFilledPath, type TbErfassen } from '../../../flows/tb'
import type { CoachingVoucherRef, CoachOption } from '@/core/data/coachings'
import {
  realUe,
  teilnahmeWochen,
  teilnahmeEnde,
  spanWochen,
  formatWochen,
} from '../../../lib/teilnahmeEnde'
import { parseMaxDauer, maxEndeDatum } from '../../../lib/maxDauer'
import { buildUeCapNote, endeCapOverride } from '../../../lib/avgsCapNotes'
import { formatFieldHint, gueltigkeitHint } from '@/core/lib/fieldError'
import { autofillReady, pickEnde, type MassnahmeCalc } from '../../../lib/tbAutofillGate'
import { mergeMassnahmeOptions } from '@/core/lib/massnahmeOptions'
import { getMassnahmeTbFields, listMassnahmen } from '@/core/data/massnahmen'
import { getCoachingVoucherRef, listCoaches, getJourneyPosition } from '@/core/data/coachings'
import { me } from '@/core/data/session'
import { AUFGABE } from '@/core/data/aufgabe'
import { downloadStaffDocument } from '../../../lib/downloadStaffDocument'
import { handleFailure } from '@/core/lib/toast'
import { ModalHeader } from '@/core/components/modal/ModalHeader'
import { Button, Callout, Icon, ScrollBar, toastError, toastInfo, toastSuccess } from 'admination-react'
import { PdfRegion } from '@/core/components/modal/PdfRegion'

const MASSNAHME_PATH = 'Maßnahme.Bezeichnung der Maßnahme'
const BEGINN_PATH = 'Maßnahme.Beginn der Teilnahme'
const ENDE_PATH = 'Maßnahme.Ende der Teilnahme'
const COACH_PATH = 'Maßnahme.Coach auswählen'
const SECOND_COACH_PATH = 'Maßnahme.Zweiter Coach (optional)'

export function TbDokument({ coachingId }: { coachingId: string | null }) {
  const { state, dispatch, reloadDashboard } = useApp()
  const [erfassen, setErfassen] = useState<TbErfassen | null | undefined>(undefined)
  useEffect(() => {
    let cancelled = false
    setErfassen(undefined)
    if (!coachingId) return
    loadTbErfassen(coachingId)
      .then((next) => { if (!cancelled) setErfassen(next) })
      .catch((e) => {
        console.error("Einreichungsweg (TB-Ansicht) fehlgeschlagen:", e)
        toastError("Einreichungsweg konnte nicht geladen werden. Bitte erneut öffnen.")
      })
    return () => { cancelled = true }
  }, [coachingId])
  const scrollerRef = useRef<HTMLElement>(null)
  const [values, setValues] = useState<FormValues | null>(null)
  const ready = values !== null
  const [attempted, setAttempted] = useState(false)
  const autofillArmed = useRef(false)

  const [view, setView] = useState<'form' | 'doc'>('form')
  const landedFor = useRef<string | null | undefined>(undefined)
  function selectView(next: 'form' | 'doc') {
    landedFor.current = coachingId
    setView(next)
  }
  const generating = !!coachingId && state.tbGenerating.includes(coachingId)
  const [confirming, setConfirming] = useState(false)
  const [closing, setClosing] = useState(false)
  const busy = generating || confirming || closing
  const submitting = useRef(false)
  const operation = useRef(0)
  const alive = useRef(true)
  useLayoutEffect(() => {
    alive.current = true
    submitting.current = false
    setConfirming(false)
    setClosing(false)
    return () => {
      alive.current = false
      operation.current += 1
    }
  }, [coachingId, state.screen?.tab])

  function current(nr: number) { return alive.current && operation.current === nr }

  const [isOwner, setIsOwner] = useState(false)
  useEffect(() => {
    me()
      .then((m) => setIsOwner(m.isOwner))
      .catch((e) => console.error("me() failed:", e))
  }, [])

  const setField = useCallback((path: string, v: unknown) => {
    if (submitting.current) return
    if (path === BEGINN_PATH || path === MASSNAHME_PATH) autofillArmed.current = true
    setValues((prev) => {
      if (!prev) return prev
      const next = structuredClone(prev)
      setAt(next as Record<string, unknown>, path, v)
      if (path === COACH_PATH && getAt(next, SECOND_COACH_PATH) === v) setAt(next, SECOND_COACH_PATH, '')
      return next
    })
  }, [])

  const [voucher, setVoucher] = useState<CoachingVoucherRef | null>(null)
  useEffect(() => {
    let cancelled = false
    setVoucher(null)
    if (!coachingId) return
    getCoachingVoucherRef(coachingId)
      .then((ref) => {
        if (!cancelled && ref) setVoucher(ref)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [coachingId])
  const maxUe = voucher?.zeitl_umf_max_ue ?? null

  const check = useMemo(
    () =>
      values
        ? checkTb(
            values,
            voucher && { beginn: voucher.gueltigkeit_beginn, ende: voucher.gueltigkeit_ende },
            voucher?.max_dauer,
          )
        : null,
    [values, voucher],
  )

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const saved = coachingId ? await loadTbForm(coachingId) : null
      const init = initialFormValues(traegerbestaetigungForm, saved)
      if (!cancelled) {
        autofillArmed.current = false
        setValues(init)
        setAttempted(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [coachingId])

  const massnahmeLabel = values ? String(getAt(values, MASSNAHME_PATH) ?? '').trim() : ''
  type MassnahmeCalcFields = { ueAnzahl: number; zeitlUmfang: string }
  const [massnahmeCalc, setMassnahmeCalc] = useState<MassnahmeCalc<MassnahmeCalcFields>>(null)
  useEffect(() => {
    let cancelled = false
    setMassnahmeCalc(null)
    if (!massnahmeLabel) return
    const label = massnahmeLabel
    getMassnahmeTbFields(label)
      .then((m) => {
        if (cancelled) return
        setMassnahmeCalc({
          label,
          fields: m ? { ueAnzahl: m.ue_anzahl, zeitlUmfang: m.zeitl_umfang } : null,
        })
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [massnahmeLabel])

  const resolvedFields =
    massnahmeCalc && massnahmeCalc.label === massnahmeLabel ? massnahmeCalc.fields : null

  const wochen =
    resolvedFields && maxUe != null
      ? teilnahmeWochen(resolvedFields.ueAnzahl, maxUe, resolvedFields.zeitlUmfang)
      : null

  const beginn = values ? String(getAt(values, BEGINN_PATH) ?? '') : ''
  const endeValue = values ? String(getAt(values, ENDE_PATH) ?? '') : ''
  useEffect(() => {
    if (!autofillArmed.current || !beginn) return
    if (!autofillReady(massnahmeLabel, massnahmeCalc)) return
    const calcEnde = wochen != null ? teilnahmeEnde(beginn, wochen) : null
    const dauer = voucher?.max_dauer ? parseMaxDauer(voucher.max_dauer) : null
    const maxEnde = dauer ? maxEndeDatum(beginn, dauer) : null
    const ende = pickEnde(calcEnde, maxEnde)
    if (!ende) return
    autofillArmed.current = false
    setField(ENDE_PATH, ende)
  }, [values, beginn, massnahmeLabel, massnahmeCalc, wochen, voucher, setField])

  const [massnahmeCatalog, setMassnahmeCatalog] = useState<{
    ueByBezeichnung: Map<string, number>
    labels: string[]
  } | null>(null)
  useEffect(() => {
    let cancelled = false
    listMassnahmen()
      .then((rows) => {
        if (!cancelled) {
          setMassnahmeCatalog({
            ueByBezeichnung: new Map(rows.map((r) => [r.label, r.ue_anzahl])),
            labels: rows.map((r) => r.label),
          })
        }
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])
  const [coachCatalog, setCoachCatalog] = useState<CoachOption[] | null>(null)
  const optionLabel = useCallback(
    (path: string, option: string) => {
      if (path === COACH_PATH || path === SECOND_COACH_PATH) {
        const coach = coachCatalog?.find((candidate) => candidate.id === option)
        if (!coach) return option
        const duplicate = coachCatalog?.some((candidate) => candidate.id !== coach.id && candidate.name === coach.name)
        return duplicate ? `${coach.name} (${coach.email || coach.id})` : coach.name || "Coach ohne Namen"
      }
      if (path !== MASSNAHME_PATH) return option
      const ue = massnahmeCatalog?.ueByBezeichnung.get(option)
      if (ue == null || maxUe == null) return option
      const cap = buildUeCapNote(ue, maxUe)
      if (cap) return `${cap.label} ${option}`
      return `(${realUe(ue, maxUe)} UE) ${option}`
    },
    [massnahmeCatalog, maxUe, coachCatalog],
  )
  const massnahmeCatalogEmpty = massnahmeCatalog !== null && massnahmeCatalog.labels.length === 0
  const goToMassnahmen = () => {
    dispatch({ type: 'CLOSE' })
    dispatch({ type: 'SET_VIEW', view: 'massnahmen' })
  }
  useEffect(() => {
    let cancelled = false
    listCoaches()
      .then((rows) => {
        if (!cancelled) setCoachCatalog(rows)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])
  const coachLabel = values ? String(getAt(values, COACH_PATH) ?? '').trim() : ''
  const secondCoachLabel = values ? String(getAt(values, SECOND_COACH_PATH) ?? '').trim() : ''
  const optionsFor = useCallback(
    (path: string, templateOptions: string[]) => {
      if (path === MASSNAHME_PATH) {
        return mergeMassnahmeOptions(massnahmeCatalog?.labels ?? null, templateOptions, massnahmeLabel)
      }
      if (path === COACH_PATH) {
        return mergeMassnahmeOptions(coachCatalog?.map((coach) => coach.id) ?? null, templateOptions, coachLabel)
      }
      if (path === SECOND_COACH_PATH) {
        return mergeMassnahmeOptions(coachCatalog?.filter((coach) => coach.id !== coachLabel).map((coach) => coach.id) ?? null, templateOptions, secondCoachLabel)
      }
      return templateOptions
    },
    [massnahmeCatalog, massnahmeLabel, coachCatalog, coachLabel, secondCoachLabel],
  )

  const hints = useMemo<FieldHints | undefined>(() => {
    const entries: [string, FieldHint][] = []

    if (voucher?.gueltigkeit_beginn && voucher.gueltigkeit_ende) {
      entries.push([
        BEGINN_PATH,
        {
          tone: 'info',
          text: formatFieldHint(gueltigkeitHint(voucher.gueltigkeit_beginn, voucher.gueltigkeit_ende)),
        },
      ])
    }

    const massnahmeCap =
      resolvedFields && maxUe != null ? buildUeCapNote(resolvedFields.ueAnzahl, maxUe) : null
    if (massnahmeCap) entries.push([MASSNAHME_PATH, { tone: 'warning', text: massnahmeCap.hint }])

    const dauer = voucher?.max_dauer ? parseMaxDauer(voucher.max_dauer) : null
    const maxEnde = dauer ? maxEndeDatum(beginn, dauer) : null

    const span = spanWochen(beginn, endeValue)
    if (span != null) {
      const override = endeCapOverride(beginn, endeValue, wochen, maxEnde)
      entries.push([ENDE_PATH, override ?? { tone: 'success', text: formatWochen(span) }])
    } else if (resolvedFields && maxUe != null) {
      if (wochen == null) {
        entries.push([
          ENDE_PATH,
          {
            tone: 'warning',
            text: "Automatische Berechnung nicht möglich: „Zeitlicher Umfang pro Woche\" der Maßnahme endet nicht auf „= x UE\"",
          },
        ])
      } else {
        entries.push([ENDE_PATH, { tone: 'success', text: formatWochen(wochen) }])
      }
    }

    return entries.length ? new Map(entries) : undefined
  }, [beginn, endeValue, resolvedFields, maxUe, wochen, voucher])

  const dateLimits = useMemo<DateFieldLimits | undefined>(() => {
    if (!voucher?.gueltigkeit_beginn || !voucher.gueltigkeit_ende) return undefined
    const window = { minIso: voucher.gueltigkeit_beginn, maxIso: voucher.gueltigkeit_ende }
    return new Map([
      [BEGINN_PATH, window],
      [ENDE_PATH, window],
    ])
  }, [voucher])

  const save = useCallback(
    (v: FormValues) => {
      if (!coachingId) return Promise.resolve()
      return saveTbDraft(coachingId, v).catch((error) => {
        console.error("Trägerbestätigung speichern fehlgeschlagen:", error)
        throw error
      })
    },
    [coachingId],
  )
  const { flush, status } = useAutosave(values, ready && !!coachingId && !busy, (v) => v ? save(v) : Promise.resolve(), 250, { form: "TbDokument", record: coachingId ?? undefined, label: "Trägerbestätigung", recover: setValues })

  async function dismiss() {
    if (submitting.current) return
    const pending = flush()
    if (!pending) { dispatch({ type: 'DISMISS' }); return }
    const nr = ++operation.current
    submitting.current = true
    setClosing(true)
    try {
      await pending
      if (current(nr)) dispatch({ type: 'DISMISS' })
    } catch (error) { void error } finally {
      if (current(nr)) { submitting.current = false; setClosing(false) }
    }
  }

  const [path] = useCoachingString(coachingId, getTbFilledPath, !generating)

  const [journeyPosition, setJourneyPosition] = useState<number | null | undefined>(undefined)
  useEffect(() => {
    let cancelled = false
    setJourneyPosition(undefined)
    if (!coachingId) {
      setJourneyPosition(null)
      return
    }
    getJourneyPosition(coachingId)
      .then(({ position }) => {
        if (!cancelled) setJourneyPosition(position)
      })
      .catch((e) => {
        console.error("Journey-Position (TB-Ansicht) fehlgeschlagen:", e)
        if (!cancelled) setJourneyPosition(null)
      })
    return () => {
      cancelled = true
    }
  }, [coachingId])

  useEffect(() => {
    if (path === undefined || journeyPosition === undefined) return
    if (landedFor.current === coachingId) return
    landedFor.current = coachingId
    if (status === 'error' || status === 'saving') {
      setView('form')
      return
    }
    if (journeyPosition !== null) {
      setView(path && journeyPosition >= AUFGABE.tbPruefen.position ? 'doc' : 'form')
      return
    }
    setView(path ? 'doc' : 'form')
  }, [path, journeyPosition, coachingId, status])

  async function saveClose() {
    if (!coachingId || busy || submitting.current) return
    submitting.current = true
    const nr = ++operation.current
    const identity = captureWriteIdentity()
    setConfirming(true)
    try {
      await flush()
      assertWriteIdentity(identity)
      if (!current(nr)) return
      await confirmTb(coachingId, identity)
      assertWriteIdentity(identity)
      if (!current(nr)) return
      reloadDashboard()
      dispatch({ type: 'CLOSE' })
    } catch (error) {
      if (current(nr)) toastError(friendlyErrorMessage(error, "Trägerbestätigung bestätigen fehlgeschlagen."))
    } finally {
      if (current(nr)) {
        submitting.current = false
        setConfirming(false)
      }
    }
  }

  function advance() {
    if (!coachingId || !values || submitting.current || confirming) return
    if (generating) {
      toastInfo("Die Trägerbestätigung wird bereits erstellt - einen Moment.")
      return
    }
    setAttempted(true)
    gateAdvance(check, async () => {
      submitting.current = true
      const nr = ++operation.current
      const identity = captureWriteIdentity()
      dispatch({ type: 'TB_GENERATING', coachingId, generating: true })
      selectView('doc')
      try {
        await flush()
        assertWriteIdentity(identity)
        if (!current(nr)) return
        const result = await generateFilledTb(coachingId, values, identity)
        assertWriteIdentity(identity)
        if (!current(nr)) return
        if (result.ok) {
          toastSuccess("Die Trägerbestätigung wurde erfolgreich erstellt.")
          reloadDashboard()
        } else {
          handleFailure(result)
          selectView('form')
        }
      } catch (error) {
        if (current(nr)) {
          toastError(friendlyErrorMessage(error, "Trägerbestätigung erstellen fehlgeschlagen."))
          selectView('form')
        }
      } finally {
        dispatch({ type: 'TB_GENERATING', coachingId, generating: false })
        if (current(nr)) submitting.current = false
      }
    })
  }

  function weiterZumVertrag() {
    if (!coachingId || !values || submitting.current || confirming) return
    setAttempted(true)
    gateAdvance(check, async () => {
      submitting.current = true
      const nr = ++operation.current
      const identity = captureWriteIdentity()
      setConfirming(true)
      try {
        await flush()
        assertWriteIdentity(identity)
        if (!current(nr)) return
        await saveTbForm(coachingId, values, identity)
        assertWriteIdentity(identity)
        if (!current(nr)) return
        await confirmTb(coachingId, identity)
        assertWriteIdentity(identity)
        if (!current(nr)) return
        reloadDashboard()
        dispatch({ type: 'CHANGE_PHASE', target: { modal: 'vertrag', tab: 'vertrag' } })
      } catch (error) {
        if (current(nr)) toastError(friendlyErrorMessage(error, "Trägerbestätigung speichern fehlgeschlagen."))
      } finally {
        if (current(nr)) {
          submitting.current = false
          setConfirming(false)
        }
      }
    })
  }

  const shownView = erfassen ? 'form' : view
  return (
    <>
      <ModalHeader size={shownView === 'doc' ? 'document' : 'form'} closeDisabled={busy} onClose={() => { void dismiss() }}>
        <div className="pill-cluster">
          {erfassen === undefined ? null : erfassen ? (
            !massnahmeCatalogEmpty && (
              <div className="pill">
                <Button primaryAction type="button" loading={confirming} disabled={!ready || busy} onClick={weiterZumVertrag}>
                  <Icon name="i-arrow-right" />
                  Weiter zum Vertrag</Button>
              </div>
            )
          ) : view === 'form' || generating ? (
            !massnahmeCatalogEmpty && (
              <ModalAction
                icon="i-doc"
                primaryAction
                title="Dokument erstellen"
                loading={generating}
                disabled={busy}
                onClick={advance}
              />
            )
          ) : (
            <>
              {!generating && path && <div className="pill">
                <Button
                  type="button"
                  iconOnly
                  title="Herunterladen"
                  disabled={busy}
                  onClick={() => downloadStaffDocument(path, coachingId, "TB")}
                >
                  <Icon name="i-download" />
                </Button>
                <Button
                  type="button"
                  iconOnly
                  title="Korrigieren"
                  disabled={busy}
                  onClick={() => selectView('form')}
                >
                  <Icon name="i-edit" />
                </Button>
              </div>}
              <ModalAction
                icon="i-check"
                primaryAction
                title="Bestätigen"
                loading={confirming}
                disabled={busy}
                onClick={saveClose}
              />
            </>
          )}
        </div>
      </ModalHeader>
      {shownView === 'form' ? (
        <>
          <main className="form" data-scroll-host ref={scrollerRef}>
            {erfassen && <ErfassenCallout erfassen={erfassen} />}
            {massnahmeCatalogEmpty ? (
              <div className="section">
                <div className="section-body">
                  <p>Die Organisation hat noch keine Maßnahmen angelegt.</p>
                  {isOwner && (
                    <Button type="button" isActive onClick={goToMassnahmen}>
                      Zu den Maßnahmen</Button>
                  )}
                </div>
              </div>
            ) : (
              values && (
                <fieldset className="form-contents" disabled={busy}>
                  <FormRenderer
                      definition={traegerbestaetigungForm}
                      values={values}
                      onChange={setField}
                      errors={check?.errors}
                      attemptedAdvance={attempted}
                      field={(field) => ({
                        required: tbManualFields?.has(field.id),
                        missing: tbManualFields?.has(field.id) ? 'mark' : undefined,
                        hint: hints?.get(field.id),
                        ...dateLimits?.get(field.id),
                        options: field.control === 'select' ? optionsFor(field.id, field.options ?? []) : undefined,
                        optionLabel: field.control === 'select' ? (option: string) => optionLabel(field.id, option) : undefined,
                      })}
                    />
                </fieldset>
              )
            )}
          </main>
          <ScrollBar scrollerRef={scrollerRef} />
        </>
      ) : (
        <PdfRegion
          path={path ?? null}
          waiting={generating || path === undefined}
          loadingLabel={generating ? "Trägerbestätigung wird ausgefüllt …" : "Trägerbestätigung wird geladen …"}
          placeholder="Noch kein Dokument vorhanden — bitte zuerst eine Bestätigung im vorherigen Schritt hochladen."
        />
      )}
    </>
  )
}

function ErfassenCallout({ erfassen }: { erfassen: TbErfassen }) {
  if (!erfassen.jobcenter && erfassen.verworfen) {
    return (
      <Callout tone="warning" role="alert" icon={<Icon name="i-bolt" />} title={`${erfassen.kostentraeger}: Admination füllt diese Trägerbestätigung nicht aus.`}>
        Das Original muss vor Maßnahmebeginn bei der Agentur vorliegen (Weisungen der BA zu §§ 81–87a SGB III). Die mit dem AVGS hochgeladene Trägerbestätigung wurde verworfen. Erfassen Sie hier die Angaben aus dem Original.</Callout>
    )
  }
  return (
    <Callout tone="info" icon={<Icon name="i-info" />} title={`Sie reichen das Original ${erfassen.jobcenter ? "beim" : "bei der"} ${erfassen.kostentraeger} ein.`}>
      Übertragen Sie die Angaben aus dem Original. Vertrag, Anwesenheitsliste und Abschlussbericht übernehmen sie von hier. Admination erstellt keine PDF der Trägerbestätigung.</Callout>
  )
}
